import type { ReactNode, RefObject } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CaretDown } from '@phosphor-icons/react';

import { apiGet, clearCredentials, UnauthorizedError } from '../lib/api';
import { formatMonth } from '../lib/format';

/**
 * Dark forest-green "hero" page header — the signature Ruta page-top surface.
 * Title + subtitle stack above the action row on mobile, sit inline on sm+.
 */
export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 rounded-xl bg-forest px-5 py-5 shadow-hero sm:px-6 sm:py-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold leading-tight tracking-tight text-white sm:text-3xl">
            {title}
          </h1>
          {subtitle && <p className="mt-1 text-sm text-white/65">{subtitle}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <section className="mb-6 rounded-xl border border-stone-200 bg-white p-4 shadow-card sm:p-5">
      <div className="mb-4">
        <h2 className="text-sm font-bold text-stone-900">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-stone-500">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

export function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  /** Formatted number, or a React node when the value needs its own tone (e.g. margin colour). */
  value: ReactNode;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-card">
      <div className="text-[11px] font-medium uppercase tracking-wider text-stone-400">{label}</div>
      <div className="tabular mt-1.5 text-xl font-bold text-stone-900">{value}</div>
      {hint && <div className="mt-1 text-[11px] text-stone-400">{hint}</div>}
    </div>
  );
}

export function KeyValueRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[11px] font-medium uppercase tracking-wider text-stone-400">{label}</span>
      <span className="tabular text-right text-sm font-medium text-stone-700">{value}</span>
    </div>
  );
}

/**
 * Horizontal-scroll wrapper for wide data tables so they never break the layout
 * on small screens. Cells should use `whitespace-nowrap`.
 */
export function TableScroll({ children }: { children: ReactNode }) {
  return (
    <div className="-mx-4 overflow-x-auto sm:mx-0">
      <div className="min-w-full px-4 sm:px-0">{children}</div>
    </div>
  );
}

export function LoadingState() {
  return <div className="py-16 text-center text-sm text-stone-400">Loading…</div>;
}

export function ErrorState({ error }: { error: unknown }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
      {error instanceof Error ? error.message : 'Something went wrong'}
    </div>
  );
}

/** Shared styling for the dark-hero header selects. */
const heroSelectClass =
  'rounded-md border border-white/15 bg-white/10 px-3 py-1.5 text-sm text-white outline-none transition-colors hover:bg-white/15 focus:border-white/30';

/**
 * Labeled control that sits on the dark hero header. The label makes it obvious
 * what the dropdown does — bare selects with "Last 6 months" / "All months"
 * alone are ambiguous when two sit side by side.
 */
function HeroControl({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium uppercase tracking-wider text-white/50">{label}</span>
      {children}
    </label>
  );
}

const lightSelectClass =
  'rounded-md border border-stone-200 bg-white px-2.5 py-1.5 text-sm text-stone-700 outline-none transition-colors hover:border-stone-300 focus:border-primary/40 focus:ring-2 focus:ring-primary/10';

/**
 * How far back trend charts load.
 * - `hero` — for the dark page header (other pages)
 * - `light` — for chart toolbars on a white surface
 */
export function MonthsSelect({
  value,
  onChange,
  variant = 'hero',
  label = 'Trend window',
}: {
  value: number;
  onChange: (months: number) => void;
  variant?: 'hero' | 'light';
  label?: string;
}) {
  const select = (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className={variant === 'hero' ? heroSelectClass : lightSelectClass}
      aria-label={label}
    >
      {[3, 6, 12, 24].map((m) => (
        <option key={m} value={m} className={variant === 'hero' ? 'bg-forest text-white' : undefined}>
          Last {m} months
        </option>
      ))}
    </select>
  );

  if (variant === 'light') {
    return (
      <label className="inline-flex items-center gap-2 text-xs text-stone-500">
        <span className="font-medium uppercase tracking-wider">{label}</span>
        {select}
      </label>
    );
  }

  return <HeroControl label={label}>{select}</HeroControl>;
}

/**
 * Month drill-down for summary cards / tables. Charts still span the full
 * {@link MonthsSelect} trend window.
 *
 * - `null` = the empty option (label from `emptyLabel`)
 * - specific YYYY-MM = freeze summary on that month
 *
 * Callers choose the empty semantics via `emptyLabel`:
 * - "All months" — aggregate over the loaded window (orgs, payments)
 * - "Latest month" — show the most recent month (overview, pricing)
 */
export function MonthPicker({
  months,
  value,
  onChange,
  emptyLabel = 'All months',
  controlLabel = 'Summary month',
}: {
  months: string[];
  value: string | null;
  onChange: (month: string | null) => void;
  /** Label for the empty/null option. */
  emptyLabel?: string;
  /** Short label above the select explaining what it controls. */
  controlLabel?: string;
}) {
  const sorted = [...months].sort((a, b) => b.localeCompare(a));
  return (
    <HeroControl label={controlLabel}>
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} className={heroSelectClass}>
        <option value="" className="bg-forest text-white">
          {emptyLabel}
        </option>
        {sorted.map((m) => (
          <option key={m} value={m} className="bg-forest text-white">
            {formatMonth(m)}
          </option>
        ))}
      </select>
    </HeroControl>
  );
}

/**
 * Close a popover on a click outside `ref` or on Escape, while `open`. The one
 * place admin popovers get that behaviour.
 */
export function useDismiss(ref: RefObject<HTMLElement | null>, open: boolean, onDismiss: () => void) {
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (ref.current && e.target instanceof Node && !ref.current.contains(e.target)) onDismiss();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [ref, open, onDismiss]);
}

/**
 * Hero month control that reads as display text (large month name) but opens a
 * picker on click. Use when the month is part of the page title treatment —
 * not a form control.
 */
export function MonthTextPicker({
  months,
  value,
  onChange,
  label = 'Month',
}: {
  months: string[];
  /** Active YYYY-MM (always a concrete month). */
  value: string;
  onChange: (month: string) => void;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const sorted = [...months].sort((a, b) => b.localeCompare(a));

  useDismiss(rootRef, open, () => setOpen(false));

  return (
    <div ref={rootRef} className="relative text-right">
      <div className="text-[10px] font-medium uppercase tracking-widest text-white/45">{label}</div>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
        className="group mt-0.5 inline-flex items-baseline gap-1.5 text-right outline-none focus-visible:ring-2 focus-visible:ring-white/30 focus-visible:ring-offset-2 focus-visible:ring-offset-forest"
      >
        <span className="font-display text-xl font-bold tracking-tight text-white underline decoration-white/25 decoration-1 underline-offset-[6px] transition-colors group-hover:decoration-white/55 sm:text-2xl">
          {formatMonth(value)}
        </span>
        <CaretDown
          className={`h-3.5 w-3.5 shrink-0 text-white/55 transition-transform group-hover:text-white/80 ${open ? 'rotate-180' : ''}`}
          weight="bold"
          aria-hidden
        />
      </button>
      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          className="absolute right-0 top-full z-40 mt-2 max-h-64 min-w-[10rem] overflow-y-auto rounded-xl border border-stone-200 bg-white py-1 shadow-hero"
        >
          {sorted.map((m) => {
            const selected = m === value;
            return (
              <li key={m} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(m);
                    setOpen(false);
                  }}
                  className={`flex w-full px-3.5 py-2 text-left text-sm transition-colors hover:bg-stone-50 ${
                    selected ? 'font-semibold text-forest' : 'font-medium text-stone-700'
                  }`}
                >
                  {formatMonth(m)}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Query a platform-operator endpoint; on expired/invalid credentials, clear
 * them and bounce to the login page.
 */
export function useInsightsQuery<T>(key: unknown[], path: string) {
  const navigate = useNavigate();
  const query = useQuery({ queryKey: key, queryFn: () => apiGet<T>(path) });

  useEffect(() => {
    if (query.error instanceof UnauthorizedError) {
      clearCredentials();
      navigate('/login');
    }
  }, [query.error, navigate]);

  return query;
}
